import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { ImageScaleView } from '@play-co/timestep-core/ui';
import { ProductId } from 'src/replicant/iap';
import StateObserver from 'src/StateObserver';
import { setPurchaseShownTime } from 'src/state/analytics';
import Scroll from 'src/game/components/shared/Scroll';
import { Match2ShopBundlesPack } from './Match2ShopBundlesPack';
import { Match2ShopBundlesGems } from './Match2ShopBundlesGems';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { parseAmount } from 'src/replicant/utils/beautifyNumber';
import { startPurchaseFlow } from 'src/lib/iap/iap.util';
import { shopBundlesIsPack } from 'src/replicant/match2/shopbundles/match2-shopbundles.getters';
import { getScreenDimensions, getScreenTop } from 'src/lib/utils';
import { DeepLink, createPopupDeeplink } from 'src/game/launch/deepLinks';
import type { CrossplayTracking } from 'src/game/crossplay/crossplay.types';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import { AssetGroup } from '@play-co/timestep-core';
import { createEmitter } from 'src/lib/Emitter';
import { DisclaimerPanel } from 'src/game/minigame/match2-timestep/shopbundles/DisclaimerPanel';
import { isInLineMiniApp } from '@play-co/gcinstant';

type CloseHandler = (result: 'success' | 'close') => void;

export type PopupMatch2ShopBundlesArgs = {
  products: ProductId[];
  allProducts?: ProductId[];
  additionalTracking?: Record<string, string>;
  additionalContext?: DeepLink[];
  crossplayTracking: CrossplayTracking;
};

export default class PopupMatch2ShopBundles extends PopupBasicLazy {
  private footer: ImageScaleView;
  private disclaimer?: DisclaimerPanel;
  private scroll: Scroll<string>;
  private buttonBack: ButtonScaleViewWithText;
  private buttonMore: ButtonScaleViewWithText;
  private gemsBase: ImageScaleView;
  private gemsText: LangBitmapFontTextView;
  private gemsIcon: ImageScaleView;
  private close: CloseHandler;
  private products: ProductId[] = [];
  private allProducts?: ProductId[];
  private args?: PopupMatch2ShopBundlesArgs;

  constructor(opts: { superview: View; close: CloseHandler }) {
    const screen = getScreenDimensions();
    const width = opts.superview.style.width;

    // Somehow superview.style.height does not fill the screen vertically
    // const height = opts.superview.style.height;
    const height = screen.height;

    super({
      superview: opts.superview,
      zIndex: 10000,
      close: () => opts.close('close'),
      width,
      height,
      darkerBg: true,
      skipMessage: true,
      tracking: {
        feature: 'gems-shop-bundles',
        $subFeature: 'gems-shop-bundles-popup',
      },
      boxOpts: {
        image: 'assets/ui/match2/shopbundles/shopbundles_bg.png',
        clip: true,
      },
      titleBannerType: 'none',
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();

    const pattern = new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/match2/shopbundles/shopbundles_pattern.png',
      scaleMethod: 'tile',
      columns: 4,
      width,
      height,
    });

    this.gemsBase = new ImageScaleView({
      superview: this.box,
      zIndex: 1000,
      width: 187,
      height: 42,
      x: 450,
      y: 50,
      image: 'assets/ui/hud/frames/hud_label_bg.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 18, right: 18 },
        vertical: { top: 18, bottom: 18 },
      },
    });

    this.gemsText = new LangBitmapFontTextView({
      superview: this.gemsBase,
      ...uiConfig.hud.gemText,
      font: bitmapFonts(uiConfig.hud.gemText.fontName),
      align: 'center',
      size: 28,
      y: 2,
    });
    this.gemsText.localeText = () => '9999';

    this.gemsIcon = new ImageScaleView({
      superview: this.gemsBase,
      width: 60,
      height: 60,
      x: 150,
      y: -10,
      image: 'assets/ui/shared/icons/gem.png',
      scaleMethod: 'contain',
    });

    this.createDisclaimerPanel();

    const overlap = 12;

    this.scroll = new Scroll<string>({
      superview: this.box,
      createItem: (...args) => this.createItem(...args),
      firstItemOffset: this.disclaimer ? overlap + 5 : 30,
      lastItemOffset: 30,
      margin: 15,
      rect: {
        x: 15,
        y: this.disclaimer
          ? this.disclaimer.style.y + this.disclaimer.style.height - overlap
          : 100,
        width: width - 20,
        height: this.disclaimer
          ? height -
            181 +
            overlap -
            this.disclaimer.style.height -
            this.disclaimer.style.y
          : height - 230,
      },
    });

    this.footer = new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/match2/shopbundles/shopbundles_footer.png',
      scaleMethod: 'stretch',
      width: this.box.style.width * 2,
      height: 181,
      x: this.box.style.width / 2,
      y: this.box.style.height - 181 / 2,
      centerOnOrigin: true,
      centerAnchor: true,
    });

    this.buttonBack = new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      superview: this.box,
      labelOffsetY: 0,
      labelOffsetX: -9,
      fontSize: 50,
      font: bitmapFonts('Body'),
      x: this.box.style.width / 2,
      y: this.footer.style.y + 10,
      width: 228,
      height: 94.7,
      centerOnOrigin: true,
      localeText: () => i18n('basic.close'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return opts.close('close');
      },
      tracking: { feature: 'gems-shop', $subFeature: 'gems-shop-back' },
    });

    this.buttonMore = new ButtonScaleViewWithText({
      ...uiConfig.buttons.primary,
      labelOffsetY: 0,
      labelOffsetX: -9,
      fontSize: 36,
      x: 163,
      y: 50,
      font: bitmapFonts('Body'),
      visible: false,
      width: 361,
      height: 100,
      localeText: () => i18n('match2.shopBundles.moreOffers'),
      onClick: async () => this.showAllProducts(),
      tracking: { feature: 'gems-shop', $subFeature: 'gems-shop-more-offers' },
    });

    createEmitter(this.root, ({ ui }) => ui.screenSize).addListener(
      (screen) => {
        this.box.updateOpts({
          height: screen.height,
          y: getScreenTop() + height / 2,
        });
        this.footer.updateOpts({
          y: this.box.style.height - 181 / 2,
        });
        this.buttonBack.updateOpts({
          y: this.footer.style.y + 10,
        });
        pattern.updateOpts({
          height: screen.height,
        });
        this.scroll.resizeScroll({
          height: this.disclaimer
            ? height -
              155 +
              overlap -
              this.disclaimer.style.height -
              this.disclaimer.style.y
            : screen.height - 230,
        });
      },
    );
  }

  private createDisclaimerPanel() {
    // Skip if not in mini app mode
    if (!isInLineMiniApp()) return;

    const disclaimerWidth = 700;
    const disclaimerHeight = 106;

    this.disclaimer = new DisclaimerPanel({
      superview: this.box,
      width: disclaimerWidth,
      height: disclaimerHeight,
      x: (this.box.style.width - disclaimerWidth) * 0.5,
      y: 125,
      zIndex: 1000,
    });
  }

  /**
   * On dialog init
   */
  public init(args: PopupMatch2ShopBundlesArgs) {
    super.init({});
    this.args = args;

    const gems = StateObserver.getState().user.inventory.premium;
    this.gemsText.localeText = () => parseAmount(gems.free + gems.paid);

    this.products = args.products;
    this.allProducts = args.allProducts;

    const items = [...this.products] as string[];
    if (this.allProducts) {
      items.push('more');
    }

    this.scroll.setItems(items);
    StateObserver.dispatch(setPurchaseShownTime());
    this.setupTitle();
  }

  /**
   * Adapt existing title to match the shop bundles design
   */
  private setupTitle() {
    this.title.getView().updateOpts({
      width: 684,
      height: 107,
      x: 0,
      y: 70,
    });
    this.title.title.localeText = () => i18n('match2.shopBundles.title');
    this.title.title.updateOpts({
      color: '#FFFFFF',
      align: 'left',
    });
    this.title.titleContainer.updateOpts({
      y: -60,
      x: -170,
    });
    this.title.banner.updateOpts({
      image: 'assets/ui/match2/shopbundles/shopbundles_ribbon.png',
      width: 684,
      height: 107,
      y: 0,
      centerOnOrigin: true,
      centerAnchor: true,
    });
  }

  /**
   * Create a scroll item
   */
  private createItem(superview: View, _: number, itemId: string) {
    if (itemId === 'more') {
      // Add the "more" button
      this.buttonMore.updateOpts({ superview, visible: true });
      return this.buttonMore;
    }

    const productId = itemId as ProductId;
    const isPack = shopBundlesIsPack(productId);

    if (isPack) {
      // Create a pack product item
      const entry = new Match2ShopBundlesPack({
        superview,
        x: 10,
      });
      entry.setup({
        productId,
        onPurchase: () => this.buyProduct(productId),
      });
      return entry;
    } else {
      // Create a regular gem product item
      const entry = new Match2ShopBundlesGems({
        superview,
        x: 10,
      });
      entry.setup({
        productId,
        onPurchase: () => this.buyProduct(productId),
      });
      return entry;
    }
  }

  /**
   * Purchase a product by id - will fall back to fake purchase if iap is not ready
   */
  private async buyProduct(productId: ProductId) {
    sounds.sfx.play('ok');

    await startPurchaseFlow({
      productId,
      feature: this.args!.crossplayTracking.feature,
      subFeature: this.args!.crossplayTracking.subFeature,
      onComplete: (success: boolean) => {
        if (success) this.close('success');
      },
      context: [
        ...(this.args?.additionalContext || []),
        createPopupDeeplink('popupMatch2ShopBundles', this.args!),
      ],
    });
  }

  private showAllProducts() {
    // Skip if there are no more products
    if (!this.allProducts) return;

    // Get current scroll position
    const scrollY = -this.scroll.getView().getOffsetY();

    // Update scroll items including more products
    this.scroll.setItems(this.allProducts);

    // Set scroll position to the same position as before
    this.scroll.scrollTo(0, scrollY, 0);
  }

  protected getAssetsGroup() {
    const urls = AssetGroup.constructURLs([`assets/ui/match2/shopbundles/`]);
    return new AssetGroup(urls);
  }
}
